import { el } from "../lib/dom.js";
import { titled } from "../lib/environment.js";
import { navigate, onNavigationCommit, type Route } from "../lib/router.js";
import { startSharedVisit, type SharedVisit, type VisitState } from "../lib/sharedVisit.js";
import { SHARED_WORDS, sharedListScreen, sharedStateScreen, sharedStateWords } from "./sharedPage.js";
import { SHARED_WITH_ME } from "./sharedWithMe.js";
import type { Screen, ScreenHost, Shell } from "./ui.js";

/**
 * A list in the account's Lists that someone shared (g137), opened inside the
 * account's own app: the tabs, the rail and Back work as they do on the
 * account's lists. The list is the shared page's, read by its grant id and
 * the session from a copy of its own, so nothing of it reaches the account's
 * store, Today or Upcoming.
 */

const ADDED_NOW = "trayflux:added-now";

/**
 * A link's page that has just added its list hands over to the app with a
 * reload. This says so across it, for this tab only, so the app can say
 * "Added to your Lists" once the list is up.
 */
export function noteAddedNow(grantId: string): void {
  try { sessionStorage.setItem(ADDED_NOW, grantId); } catch { /* the words are a courtesy */ }
}

function takeAddedNow(grantId: string): boolean {
  try {
    if (sessionStorage.getItem(ADDED_NOW) !== grantId) return false;
    sessionStorage.removeItem(ADDED_NOW);
    return true;
  } catch {
    return false;
  }
}

export function addedListScreen(shell: Shell, grantId: string, start: typeof startSharedVisit = startSharedVisit): Screen {
  const visit: SharedVisit = start({ kind: "added", grantId });
  const node = el("div", { class: "added-list" });
  let inner: Screen | undefined;
  /** The frame's Category suggestions and sync notices, lent to this list's store while it is open. */
  let giveBack: (() => void) | undefined;
  /** The route the frame last gave, for the list once it opens: a deep link names an item. */
  let route: Route | undefined;
  let said: string | undefined;

  /**
   * Remove from my Lists, from the list's own menu: back to Lists, as a
   * deleted list goes. The entry goes only once that departure commits, so a
   * draft kept at the leaving question keeps the list too, and still syncs.
   * Asked by a local navigation, so a later tap of the person's own is never
   * taken for it.
   */
  let removal: "asked" | "leaving" | undefined;
  const stopCommit = onNavigationCommit((navigation) => {
    if (removal !== "asked") return;
    removal = navigation.own && navigation.to.name === "lists" ? "leaving" : undefined;
  });
  const remove = () => {
    removal = "asked";
    navigate("/", { local: true });
  };
  const removeNow = () => {
    const name = shell.store.addedLists().find((entry) => entry.grantId === grantId)?.name ?? SHARED_WITH_ME.unnamed;
    void shell.store.removeAddedList(grantId);
    shell.announce(SHARED_WITH_ME.removed(name));
  };

  const show = (state: VisitState): void => {
    // An open list never drops back to a state: its copy is what it shows.
    if (inner) return;
    if (state.kind !== "open") {
      const retry = state.kind === "signin" ? () => shell.reauth() : () => visit.retry();
      node.replaceChildren(sharedStateScreen(state.kind, retry).node);
      const words = sharedStateWords(state.kind);
      if (state.kind !== "loading" && words !== said) shell.announce(words);
      said = words;
      return;
    }
    const host: ScreenHost = {
      store: state.store,
      detail: shell.detail,
      announce: shell.announce,
      openPopup: shell.openPopup,
      confirm: shell.confirm,
      refreshAction: shell.refreshAction,
      narrow: shell.narrow,
      onNarrow: shell.onNarrow,
    };
    inner = sharedListScreen(host, visit, state.store, {
      signIn: () => shell.reauth(),
      // A guest's Add, which an account never sees.
      signInToAdd: () => undefined,
      // Its link ended and the copy is gone: back to the Lists it was in.
      gone: () => navigate("/"),
      inApp: { remove },
    });
    giveBack = shell.lend?.(state.store);
    node.replaceChildren(inner.node);
    if (route) inner.update?.(route);
    // The frame placed its action for the state screen, which had none.
    shell.refreshAction();
    if (takeAddedNow(grantId)) shell.announce(SHARED_WORDS.addedToLists);
  };
  const stop = visit.onState(show);
  show(visit.state());

  return {
    node,
    update(next) {
      route = next;
      inner?.update?.(next);
    },
    action: () => inner?.action?.(),
    dirty: () => inner?.dirty?.() ?? false,
    busy: () => inner?.busy?.() ?? false,
    returnTo: (itemId) => inner?.returnTo?.(itemId),
    dispose() {
      stop();
      stopCommit();
      inner?.dispose();
      giveBack?.();
      void visit.close();
      // The account's screens set no title of their own.
      document.title = titled();
      if (removal === "leaving") removeNow();
    },
  };
}
